// ============================================================================
// SortableTable (Chunk 7G)
// ----------------------------------------------------------------------------
// Shared helpers for click-to-sort table columns. Originated in Admin.js;
// lifted here so every list page (Companies, Contacts, ClaimsList, Reports)
// uses the same sort affordance, arrow indicator, and null-safe ordering.
//
// Usage:
//   const [sort, setSort] = useState({ key: "name", dir: "asc" });
//   <SortableTh label="Name" col="name" sort={sort} setSort={setSort} />
//   const sorted = sortRows(rows, sort.key, sort.dir);
//
// For computed columns (e.g. Reserves - Paid), pass an `accessor` prop to
// SortableTh instead of `col`, and use sortRowsBy(rows, accessorFn, dir):
//   <SortableTh label="Remaining" col="_remaining"
//               accessor={r => (r.total_reserves||0) - (r.total_paid||0)}
//               sort={sort} setSort={setSort} />
//   const sorted = sortRowsBy(rows, sort.key, sort.dir, accessorMap);
// ============================================================================

export function SortableTh({ label, col, sort, setSort, className, style, align }) {
  const active = sort.key === col;
  const arrow = active ? (sort.dir === "asc" ? " ▲" : " ▼") : "";
  const onClick = () =>
    setSort(s => s.key === col
      ? { key: col, dir: s.dir === "asc" ? "desc" : "asc" }
      : { key: col, dir: "asc" });
  const alignClass = align === "right" ? "text-right" : align === "center" ? "text-center" : "";
  return (
    <th
      className={[className, alignClass].filter(Boolean).join(" ") || undefined}
      style={{ cursor: "pointer", userSelect: "none", whiteSpace: "nowrap", ...(style || {}) }}
      onClick={onClick}
      title="Click to sort"
    >
      {label}{arrow}
    </th>
  );
}

// Null-safe, type-aware sort. Nulls/empty always sort last regardless of direction.
// Values are normalized: strings → lowercase, null/undefined/"" → null.
export function sortRows(rows, key, dir) {
  const mult = dir === "desc" ? -1 : 1;
  const norm = v => (v === null || v === undefined || v === "") ? null
                 : (typeof v === "string" ? v.toLowerCase() : v);
  return [...rows].sort((a, b) => {
    const va = norm(a[key]); const vb = norm(b[key]);
    if (va === null && vb === null) return 0;
    if (va === null) return 1;      // nulls last
    if (vb === null) return -1;
    if (va < vb) return -1 * mult;
    if (va > vb) return  1 * mult;
    return 0;
  });
}

// Accessor-based sort for computed / nested columns. Pass an accessorMap
// keyed by sort key; falls back to rows[key] for plain fields.
//   sortRowsBy(rows, sort.key, sort.dir, { _remaining: r => (r.total_reserves||0) - (r.total_paid||0) })
export function sortRowsBy(rows, key, dir, accessorMap) {
  const fn = (accessorMap && accessorMap[key]) || (r => r[key]);
  const mult = dir === "desc" ? -1 : 1;
  const norm = v => (v === null || v === undefined || v === "") ? null
                 : (typeof v === "string" ? v.toLowerCase() : v);
  return [...rows].sort((a, b) => {
    const va = norm(fn(a)); const vb = norm(fn(b));
    if (va === null && vb === null) return 0;
    if (va === null) return 1;
    if (vb === null) return -1;
    if (va < vb) return -1 * mult;
    if (va > vb) return  1 * mult;
    return 0;
  });
}
